<template>
    <!-- Lignes de connexion pour les marqueurs éclatés -->
    <template v-for="marker in safeExpandedMarkers" :key="`line-${marker.id}`">
        <l-polyline :lat-lngs="[marker.centerCoordinates, marker.coordinates]" :color="getMarkerColor(marker.rank)"
            :weight="2" :opacity="0.6" :interactive="false" />
    </template>

    <!-- Marqueurs -->
    <l-marker v-for="marker in safeDisplayableMarkers" :key="marker.id" :lat-lng="marker.coordinates"
        :z-index-offset="getZIndexOffset(marker.rank)"
        @mouseover="() => handleLocationHover(marker.locationCode, marker.username)" @mouseout="handleLocationLeave">
        <!-- Icône customisée -->
        <l-icon
            :icon-size="[getMarkerSize(marker.rank, marker.username === hoveredUsername), getMarkerSize(marker.rank, marker.username === hoveredUsername)]"
            :icon-anchor="[getMarkerSize(marker.rank, marker.username === hoveredUsername) / 2, getMarkerSize(marker.rank, marker.username === hoveredUsername) / 2]"
            class-name="custom-marker-icon">
            <div class="marker-container" :class="{ 'marker-highlighted': marker.username === hoveredUsername }" :style="{
                width: getMarkerSize(marker.rank, marker.username === hoveredUsername) + 'px',
                height: getMarkerSize(marker.rank, marker.username === hoveredUsername) + 'px',
                backgroundColor: getMarkerFillColor(marker.rank),
                borderColor: getMarkerColor(marker.rank),
            }">
                <div class="marker-name-chip" :class="[
                    isTopThree(marker.rank) ? 'marker-name-chip-permanent' : '',
                    getNameChipClass(marker.rank)
                ]" :style="getNameChipAnimationStyle(marker.rank)">
                    <span class="name-chip-rank">#{{ marker.rank }}</span>
                    <span class="name-chip-separator">·</span>
                    <span class="name-chip-username">{{ formatUsername(marker.username, isTopThree(marker.rank) ? 18 :
                        13)
                        }}</span>
                </div>

                <span class="rank-text" :style="{ color: getTextColor(marker.rank) }">
                    {{ marker.rank }}
                </span>

                <!-- Badge de compteur si plusieurs utilisateurs dans cette région -->
                <div v-if="marker.isGrouped && marker.type === 'main'" class="group-badge"
                    :style="{ backgroundColor: getMarkerColor(marker.rank) }">
                    +{{ marker.groupCount - 1 }}
                </div>
            </div>
        </l-icon>

        <!-- Tooltip au survol -->
        <l-tooltip :options="{
            permanent: false,
            direction: 'top',
            offset: [0, -30],
        }">
            <div class="text-center">
                <div class="font-bold text-sm">{{ marker.username }}</div>
                <div class="text-xs opacity-80">
                    #{{ marker.rank }} · {{ marker.locationName }}
                </div>
                <div v-if="marker.isGrouped" class="text-xs mt-1 italic">
                    Survole pour voir les {{ marker.groupCount - 1 }} autres
                </div>
            </div>
        </l-tooltip>
    </l-marker>
</template>

<script setup>
/* global defineProps, defineEmits */
import { toRef, computed, watch, onErrorCaptured } from 'vue';
import { LMarker, LIcon, LTooltip, LPolyline } from '@vue-leaflet/vue-leaflet';
import { useTopUserMarkers } from './composables/useTopUserMarkers';

/**
 * Composant pour afficher les marqueurs des utilisateurs du top 10
 * qui ont renseigné leur localisation (région/département/commune).
 * 
 * Système d'éclatement au survol :
 * - Par défaut : un seul marqueur par localisation (le mieux classé)
 * - Au survol : les autres marqueurs apparaissent en cercle autour
 * 
 * Adapte l'affichage selon le niveau de zoom :
 * - Vue régions : affiche les utilisateurs à leur région
 * - Vue départements : affiche les utilisateurs à leur département
 * - Vue communes : affiche les utilisateurs à leur commune
 * 
 * Props:
 * - topUsers: Array des 10 premiers utilisateurs du classement
 * - currentLevel: 'regions' | 'departements' | 'communes'
 * - communesGeoJson: GeoJSON des communes (pour calculer les centroïds)
 */
const props = defineProps({
    topUsers: {
        type: Array,
        required: true,
        default: () => [],
    },
    currentLevel: {
        type: String,
        default: 'regions',
        validator: (value) => !value || ['regions', 'departements', 'communes'].includes(value),
    },
    communesGeoJson: {
        type: Object,
        default: null,
    },
    hoveredUsernameProp: {
        type: String,
        default: null,
    },
    leafletMap: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['user-hover']);

// Logique de gestion des marqueurs
const {
    displayableMarkers,
    hoveredUsername,
    handleLocationHover,
    handleLocationLeave,
} = useTopUserMarkers(
    toRef(props, 'topUsers'),
    toRef(props, 'currentLevel'),
    toRef(props, 'communesGeoJson'),
    toRef(props, 'hoveredUsernameProp'),
    toRef(props, 'leafletMap')
);

// Émettre l'événement quand un utilisateur est survolé
watch(hoveredUsername, (newUsername) => {
    emit('user-hover', newUsername);
});

// Capturer les erreurs Leaflet silencieusement pendant les transitions
onErrorCaptured((err) => {
    console.warn('Erreur capturée dans TopUserMarkers:', err.message);
    // Retourner false empêche la propagation de l'erreur
    return false;
});

/**
 * Valide qu'un marker a des coordonnées valides pour Leaflet
 * @param {Object} marker - Le marker à valider
 * @param {boolean} checkCenter - Vérifier aussi centerCoordinates (pour lignes)
 */
function isValidMarker(marker, checkCenter = false) {
    if (!marker) return false;

    // Vérifier coordinates
    const coords = marker.coordinates;
    if (!coords || !Array.isArray(coords) || coords.length !== 2) return false;
    if (typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return false;
    if (isNaN(coords[0]) || isNaN(coords[1])) return false;

    // Vérifier centerCoordinates si demandé (pour les lignes)
    if (checkCenter && marker.centerCoordinates) {
        const centerCoords = marker.centerCoordinates;
        if (!Array.isArray(centerCoords) || centerCoords.length !== 2) return false;
        if (typeof centerCoords[0] !== 'number' || typeof centerCoords[1] !== 'number') return false;
        if (isNaN(centerCoords[0]) || isNaN(centerCoords[1])) return false;
    }

    return true;
}

// Filtrer les marqueurs pour ne garder que les valides (protection supplémentaire)
const safeDisplayableMarkers = computed(() => {
    if (!displayableMarkers.value) return [];
    return displayableMarkers.value.filter(m => isValidMarker(m));
});

// Marqueurs éclatés (pour les lignes de connexion) - filtrés
const safeExpandedMarkers = computed(() => {
    return safeDisplayableMarkers.value.filter(m => m.type === 'expanded' && isValidMarker(m, true));
});

/**
 * Détermine le z-index du marqueur
 * Les rangs les plus élevés (1, 2, 3) doivent être AU-DESSUS
 * Plus le numéro de rang est petit, plus le z-index est grand
 */
function getZIndexOffset(rank) {
    // Rang 1 = 1100, rang 2 = 1099, rang 3 = 1098, etc.
    return 1100 - rank;
}

/**
 * Détermine la taille du marqueur en fonction du rang
 * Plus le rang est élevé (1=meilleur), plus le cercle est grand
 * @param {number} rank - Le rang de l'utilisateur
 * @param {boolean} isHighlighted - Si le marqueur est mis en évidence (survolé depuis la liste)
 */
function getMarkerSize(rank, isHighlighted = false) {
    let baseSize;
    if (rank === 1) baseSize = 50;
    else if (rank === 2) baseSize = 46;
    else if (rank === 3) baseSize = 42;
    else if (rank <= 5) baseSize = 38;
    else baseSize = 34;

    // Augmenter légèrement la taille si mis en évidence
    return isHighlighted ? baseSize + 8 : baseSize;
}

/**
 * Détermine la couleur de bordure du marqueur
 * Respecte la charte graphique définie dans tailwind.config.js
 */
function getMarkerColor(rank) {
    if (rank === 1) return '#D4AF37'; // card-gold-premium border
    if (rank === 2) return '#6B7280'; // card-gold border
    if (rank === 3) return '#D4AF37'; // card-secondary border
    return '#6B7280';                 // secondary
}

/**
 * Détermine la couleur de remplissage du marqueur
 * Respecte la charte graphique définie dans tailwind.config.js
 */
function getMarkerFillColor(rank) {
    if (rank === 1) return '#212121'; // card-gold-premium
    if (rank === 2) return '#D4AF37'; // card-gold
    if (rank === 3) return '#6B7280'; // card-secondary
    return '#FAEBD7';                 // background (blanc antique)
}

/**
 * Détermine la couleur du texte
 * 1er: gold sur fond noir
 * 2ème: noir sur fond gold
 * 3ème: blanc sur fond gris
 * Autres: noir sur fond beige
 */
function getTextColor(rank) {
    if (rank === 1) return '#D4AF37'; // gold sur fond noir
    if (rank === 2) return '#212121'; // noir sur fond gold
    if (rank === 3) return '#FFFFFF'; // blanc sur fond gris
    return '#212121';                 // noir sur fond beige
}

function isTopThree(rank) {
    return rank >= 1 && rank <= 3;
}

function formatUsername(username, maxLength = 14) {
    if (!username || typeof username !== 'string') return 'Utilisateur';
    if (username.length <= maxLength) return username;
    return `${username.slice(0, maxLength - 1)}…`;
}

function getNameChipClass(rank) {
    if (rank === 1) return 'marker-name-chip-rank-1';
    if (rank === 2) return 'marker-name-chip-rank-2';
    if (rank === 3) return 'marker-name-chip-rank-3';
    return 'marker-name-chip-default';
}

function getNameChipAnimationStyle(rank) {
    if (!isTopThree(rank)) return {};

    return {
        animationDelay: `${(rank - 1) * 0.12}s`,
    };
}
</script>

<style scoped>
/* Supprime le conteneur par défaut de Leaflet DivIcon */
:deep(.custom-marker-icon) {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
}

/* Conteneur du marqueur circulaire */
.marker-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border-width: 3px;
    border-style: solid;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.marker-container:hover {
    transform: scale(1.15);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
}

/* Cartouche nom/rang au-dessus du marqueur */
.marker-name-chip {
    position: absolute;
    top: -18px;
    left: 50%;
    transform: translate(-50%, -100%);
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(107, 114, 128, 0.45);
    color: #212121;
    white-space: nowrap;
    pointer-events: none;
    user-select: none;
    opacity: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.marker-container:hover .marker-name-chip,
.marker-highlighted .marker-name-chip {
    opacity: 1;
    transform: translate(-50%, calc(-100% - 2px));
}

.marker-name-chip-permanent {
    opacity: 1;
    transform: translate(-50%, calc(-100% - 3px));
    box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.2), 0 6px 16px rgba(0, 0, 0, 0.45);
    animation: chipReveal 0.45s ease-out both, nameGlow 2.6s ease-in-out infinite;
}

.marker-name-chip-rank-1 {
    background: linear-gradient(130deg, #1f1f1f 0%, #2b2b2b 65%, #1f1f1f 100%);
    border-color: #D4AF37;
    color: #FAEBD7;
}

.marker-name-chip-rank-1 .name-chip-rank {
    color: #D4AF37;
}

.marker-name-chip-rank-2 {
    background: linear-gradient(130deg, #d8b74b 0%, #D4AF37 55%, #c8a433 100%);
    border-color: #6B7280;
    color: #212121;
}

.marker-name-chip-rank-2 .name-chip-rank {
    color: #212121;
}

.marker-name-chip-rank-3 {
    background: linear-gradient(130deg, #748091 0%, #6B7280 55%, #606975 100%);
    border-color: #D4AF37;
    color: #FFFFFF;
}

.marker-name-chip-rank-3 .name-chip-rank {
    color: #FAEBD7;
}

.name-chip-rank {
    font-size: 11px;
    font-weight: 800;
    color: #212121;
}

.name-chip-separator {
    font-size: 10px;
    opacity: 0.85;
}

.name-chip-username {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2px;
}

.marker-name-chip-default {
    background: rgba(255, 255, 255, 0.96);
    border-color: rgba(107, 114, 128, 0.45);
    color: #212121;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}

/* Texte du rang au centre du cercle */
.rank-text {
    font-weight: 700;
    font-size: 18px;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    user-select: none;
    line-height: 1;
}

/* Effet de mise en évidence quand le marqueur est survolé depuis la liste */
.marker-highlighted {
    animation: markerPulse 1s ease-in-out infinite;
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.4) !important;
}

@keyframes markerPulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }
}

@keyframes nameGlow {

    0%,
    100% {
        box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.2), 0 6px 16px rgba(0, 0, 0, 0.45);
    }

    50% {
        box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.35), 0 0 14px rgba(212, 175, 55, 0.25), 0 8px 18px rgba(0, 0, 0, 0.5);
    }
}

@keyframes chipReveal {
    0% {
        opacity: 0;
        transform: translate(-50%, calc(-100% + 6px)) scale(0.96);
    }

    100% {
        opacity: 1;
        transform: translate(-50%, calc(-100% - 3px)) scale(1);
    }
}

/* Badge de compteur (nombre d'utilisateurs supplémentaires) */
.group-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: #212121;
    padding: 2px 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: pulse 2s ease-in-out infinite;
}

@media (max-width: 768px) {
    .marker-name-chip {
        top: -14px;
        padding: 3px 7px;
    }

    .name-chip-rank,
    .name-chip-username {
        font-size: 10px;
    }

}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.1);
    }
}
</style>
